import { z, ZodError } from 'zod';
import { useAppSelector } from '../../../redux/hooks';
import { useMemo } from 'react';
import {
  getAllModelFields,
  temporalJoinTypeConfig,
  useDataModelerPermittedSchemas,
} from './utils';
import {
  dataModelerSelectCurrentDraftEdits,
  dataModelerSelectCurrentSchemaObj,
  dataModelerSelectCurrentUiState,
} from '../../../redux/selectors/dataModeler';
import { defaultEmptyDraftEdits } from '../../../redux/slices/dataModeler/dataModelerSlice';
import {
  FieldClass,
  IDataSourceInput,
  ISchemaDataModelFieldsFragment,
} from '../../../utils/apollo/graphqlSchema';
import {
  IAddedFactDraftData,
  IDataModelerDraftEdits,
  IDataModelerUiState,
  IJoinDraftData,
  ITemporalJoinConditionDraftData,
} from '../../../redux/slices/dataModeler';
import { isNil } from 'lodash';
import { validateRawSql } from '../../../utils/rawSqlParser';
import { UnreachableCaseError } from 'ts-essentials';

export const SCHEMA_NAME_MAX_LENGTH = 45;

export const COLUMN_NAME_MAX_LENGTH = 255;

const VALID_COLUMN_REGEX = /^[0-9a-zA-Z_]+$/;

const STARTS_WITH_NUMBER_COLUMN_REGEX = /^[0-9].*/;

// XXX some weird cases to handle here: some column names need escaping because of keyword conflicts
// so a bunch of quotes are actually supported, but only when surrounding an otherwise valid column name
const SUPPORTED_QUOTES = '\'"`';

export const getFieldColumnSchemaValidator = (
  existingColumns: Set<string> | null,
) =>
  z
    .string()
    .min(1, 'Column cannot be empty')
    .max(
      COLUMN_NAME_MAX_LENGTH,
      `Field name cannot exceed ${COLUMN_NAME_MAX_LENGTH} characters.`,
    )
    .superRefine((value, ctx) => {
      if (STARTS_WITH_NUMBER_COLUMN_REGEX.test(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Column name can not start with a number',
          fatal: true,
        });
        return z.NEVER;
      }
      const unquoted =
        value.length > 2 &&
        SUPPORTED_QUOTES.includes(value[0]) &&
        value[0] === value[value.length - 1]
          ? value.slice(1, value.length - 1)
          : value;
      if (!VALID_COLUMN_REGEX.test(unquoted)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Column name must only contain alphanumeric characters or underscores',
          fatal: true,
        });
        return z.NEVER;
      }
      if (
        existingColumns?.has(value.toLowerCase()) ||
        existingColumns?.has(unquoted.toLowerCase())
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Field with column ${value} is already defined`,
          fatal: true,
        });
        return z.NEVER;
      }
    });

export const getFieldNameValidator = (existingNames: Set<string> | null) =>
  z
    .string()
    .min(1, 'Name cannot be empty')
    .max(
      COLUMN_NAME_MAX_LENGTH,
      `Field name cannot exceed ${COLUMN_NAME_MAX_LENGTH} characters.`,
    )
    .superRefine((value, ctx) => {
      if (existingNames?.has(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Field with name ${value} is already defined`,
          fatal: true,
        });
        return z.NEVER;
      }
    });

const useViewColumnsValidatorInternal = (
  schemaObj: ISchemaDataModelFieldsFragment | null,
  draftEdits: IDataModelerDraftEdits,
  current?: {
    viewKey?: string;
    tableKey?: string;
    tableColumn?: string;
    fieldKey?: string;
  },
) => {
  const existingFields = getAllModelFields(schemaObj);
  const existingViewColumns = useMemo(
    () => existingFields.map((field) => field.column),
    [existingFields],
  );
  const { draftFieldsData, draftDerivedFields } = draftEdits;

  const allOtherFields: { key: string; viewColumn: string; name: string }[] =
    useMemo(() => {
      const {
        viewKey: currentViewKey,
        tableKey: currentTableKey,
        tableColumn: currentTableColumn,
        fieldKey: currentFieldKey,
      } = current ?? {};
      const ret: { key: string; viewColumn: string; name: string }[] = [];
      existingFields.forEach(({ key, column, name, tableFields }) => {
        const isCurrent =
          key === currentFieldKey ||
          tableFields.some(
            (tableFeild) =>
              tableFeild.tableKey === currentTableKey &&
              tableFeild.column === currentTableColumn,
          );
        if (!isCurrent) {
          ret.push({
            key,
            viewColumn: column,
            name,
          });
        }
      });
      draftFieldsData.forEach((draftField) => {
        if (draftField.addInput) {
          const { key, viewColumn, name, viewKey, tableKey, column } =
            draftField.addInput;
          const isCurrent =
            key === currentFieldKey ||
            (viewKey === currentViewKey &&
              tableKey === currentTableKey &&
              column === currentTableColumn);
          if (!isCurrent) {
            ret.push({
              key,
              viewColumn,
              name: name ?? viewColumn,
            });
          }
        }
      });
      draftDerivedFields.forEach((derivedField) => {
        const { key, column, name } = derivedField;
        if (key !== currentFieldKey) {
          ret.push({
            key,
            viewColumn: column,
            name: name ?? column,
          });
        }
      });
      return ret;
    }, [current, draftFieldsData, draftDerivedFields, existingFields]);

  const allOtherColumns = useMemo(
    () => allOtherFields.map((fld) => fld.viewColumn),
    [allOtherFields],
  );

  const allOtherColumnsSet = useMemo(
    () => new Set(allOtherColumns),
    [allOtherColumns],
  );

  const allOtherFieldNamesSet = useMemo(
    () => new Set(allOtherFields.map((fld) => fld.name)),
    [allOtherFields],
  );
  const columnValidator = useMemo(() => {
    return z.object({
      value: getFieldColumnSchemaValidator(allOtherColumnsSet),
    });
  }, [allOtherColumnsSet]);

  const namesValidator = useMemo(() => {
    return z.object({
      value: getFieldNameValidator(allOtherFieldNamesSet),
    });
  }, [allOtherFieldNamesSet]);

  const hasDuplicates =
    allOtherColumns.length !== allOtherColumnsSet.size ||
    allOtherFields.length !== allOtherFieldNamesSet.size;

  return {
    existingFields,
    existingViewColumns,
    allOtherColumnsSet,
    allOtherFieldNamesSet,
    columnValidator,
    namesValidator,
    hasDuplicates,
  };
};

export const useViewColumnsValidator = (current?: {
  viewKey?: string;
  tableKey?: string;
  tableColumn?: string;
  fieldKey?: string;
}) => {
  const schemaObj = useAppSelector(dataModelerSelectCurrentSchemaObj);
  const draftEdits =
    useAppSelector(dataModelerSelectCurrentDraftEdits) ??
    defaultEmptyDraftEdits;

  return useViewColumnsValidatorInternal(schemaObj, draftEdits, current);
};

export const schemaDraftIsDirty = (draftEdits: IDataModelerDraftEdits | null) =>
  !!draftEdits &&
  (draftEdits.draftFieldsData.length > 0 ||
    draftEdits.draftDerivedFields.length > 0 ||
    draftEdits.editJoinsData.length > 0 ||
    draftEdits.updatedFieldsInputs.length > 0 ||
    draftEdits.addedSubjects.length > 0 ||
    draftEdits.addedFactsData.length > 0 ||
    draftEdits.addedDimensionsData.length > 0 ||
    draftEdits.addFunnelPartitions.length > 0 ||
    draftEdits.editColumnInputs.length > 0 ||
    draftEdits.isNew ||
    !!draftEdits.dataSource ||
    !!draftEdits.sampling ||
    !!draftEdits.updatedName ||
    !!draftEdits.dataRefresh ||
    !isNil(draftEdits.disabled) ||
    !!draftEdits.endDate ||
    !isNil(draftEdits.description));

export const useIsSchemaStateDirty = (
  schema: string,
): {
  isNew: boolean;
  isDirty: boolean;
} => {
  const schemaState = useAppSelector(
    (rootState) => rootState.dataModeler.schemaStates[schema],
  );
  return {
    isNew: schemaState?.draftEdits?.isNew ?? false,
    isDirty:
      schemaState?.draftEdits?.isNew ||
      schemaDraftIsDirty(schemaState?.draftEdits ?? null),
  };
};

const jsonValidator = z.string().superRefine((value: string, ctx) => {
  try {
    JSON.parse(value);
  } catch {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Invalid Json value`,
      fatal: true,
    });
    return z.NEVER;
  }
});

export const isValidDataSourceInput = (
  dataSource: IDataSourceInput | null,
): boolean => {
  if (!dataSource) {
    return false;
  }
  if (!dataSource.connection.basic && !dataSource.connection.bigQuery) {
    return false;
  }
  if (dataSource.connection.basic && !dataSource.connection.basic.privateKey) {
    const { username, password, jdbcUrl } = dataSource.connection.basic;
    if (!username || !password || !jdbcUrl) {
      return false;
    }
  }

  if (dataSource.connection.basic && dataSource.connection.basic.privateKey) {
    const { username, jdbcUrl, privateKey } = dataSource.connection.basic;
    if (!username || !privateKey.key || !jdbcUrl) {
      return false;
    }
  }

  if (dataSource.connection.bigQuery) {
    const { account, projectId, dataset, keyPlain } =
      dataSource.connection.bigQuery;
    if (!account || !projectId || !dataset || !keyPlain) {
      return false;
    }
    if (!jsonValidator.safeParse(keyPlain).success) {
      return false;
    }
  }
  return true;
};

const isInvalidTemporalCondition = (
  temporalCondition: ITemporalJoinConditionDraftData,
) => {
  if (!temporalCondition.dimTemporalColumn) {
    return true;
  }
  const jtConfig = temporalJoinTypeConfig[temporalCondition.joinType];
  switch (jtConfig.numOps) {
    case 0:
      return false;
    case 1:
      return !temporalCondition.factTemporalKey;
    case 2:
      return (
        !temporalCondition.factTemporalKey ||
        !temporalCondition.dimSecondTemporalColumn
      );
    default:
      throw new UnreachableCaseError(jtConfig.numOps);
  }
};

export const isInvalidJoinData = (joinData: IJoinDraftData) => {
  return (
    (joinData.conditions.length === 0 && !joinData.sqlCondition) ||
    (joinData.temporalCondition &&
      isInvalidTemporalCondition(joinData.temporalCondition))
  );
};

const isInvalidFactData = (
  draft: IAddedFactDraftData,
  draftEdits: IDataModelerDraftEdits | null,
) => {
  // ts and date columns must be set and event field present
  if (!draft.temporals.ts.column || !draft.temporals.date.column) {
    return true;
  }
  return !draftEdits?.draftFieldsData?.find(
    (draftField) =>
      draftField.addInput?.typeInput?.fieldClass === FieldClass.Event ||
      draftField.extendInput?.typeInput?.fieldClass === FieldClass.Event,
  );
};

export const useDataModelerEditorCanConfirmInternal = (
  schemaObj: ISchemaDataModelFieldsFragment | null,
  draftEdits: IDataModelerDraftEdits | null,
  uiState: IDataModelerUiState | null,
): boolean => {
  const { hasDuplicates } = useViewColumnsValidatorInternal(
    schemaObj,
    draftEdits ?? defaultEmptyDraftEdits,
    undefined,
  );
  const isDirty = schemaDraftIsDirty(draftEdits);
  const hasInvalidFactsData = useMemo(() => {
    const factsData = draftEdits?.addedFactsData ?? [];
    return factsData.some((fact) => isInvalidFactData(fact, draftEdits));
  }, [draftEdits]);
  const hasInvalidJoinData = useMemo(() => {
    const newJoinsData =
      draftEdits?.addedDimensionsData?.map((dimData) => dimData.joinData) ?? [];
    const editedJoinsData =
      draftEdits?.editJoinsData?.map((editJoin) => editJoin.joinData) ?? [];
    return [...newJoinsData, ...editedJoinsData].some((joinData) =>
      isInvalidJoinData(joinData),
    );
  }, [draftEdits?.addedDimensionsData, draftEdits?.editJoinsData]);
  const hasSubjects =
    (!!schemaObj?.subjects && schemaObj.subjects.length > 0) ||
    (!!draftEdits?.addedSubjects && draftEdits?.addedSubjects.length > 0);
  const isValidDataSource = draftEdits?.dataSource
    ? isValidDataSourceInput(draftEdits?.dataSource)
    : !draftEdits?.isNew;
  const hasInvalidInstallDate =
    !!draftEdits?.editTablesData.some(
      (editTable) =>
        !!editTable.installTemporals && !editTable.installTemporals.date.column,
    ) ||
    !!draftEdits?.addedFactsData.some(
      (addFact) =>
        !!addFact.installTemporals && !addFact.installTemporals.date.column,
    ) ||
    !!draftEdits?.addedDimensionsData.some(
      (addDim) =>
        !!addDim.installTemporals && !addDim.installTemporals.date.column,
    );
  const hasDraftDerivedField = !!uiState?.draftDerivedField;
  return (
    !draftEdits?.nameError &&
    !hasDuplicates &&
    !hasInvalidJoinData &&
    !hasInvalidFactsData &&
    !hasInvalidInstallDate &&
    !hasDraftDerivedField &&
    isValidDataSource &&
    hasSubjects &&
    isDirty
  );
};

export const useDataModelerEditorCanConfirm = (): boolean => {
  const draftEdits = useAppSelector(dataModelerSelectCurrentDraftEdits);
  const schemaObj = useAppSelector(dataModelerSelectCurrentSchemaObj);
  const uiState = useAppSelector(dataModelerSelectCurrentUiState);
  return useDataModelerEditorCanConfirmInternal(schemaObj, draftEdits, uiState);
};

// This would be needed if we ever want to indicate the schema "can confirm" state in the schemas navigation
// export const useDataModelerEditorCanConfirmSchema = (
//   schemaId: string,
// ): boolean => {
//   const schemaState = useAppSelector(
//     (rootState) => rootState.dataModeler.schemaStates[schemaId],
//   );
//   const schemaObj = schemaState?.schemaDataModel ?? null;
//   const draftEdits = schemaState?.draftEdits ?? null;
//   const uiState = schemaState?.uiState ?? null;
//   return useDataModelerEditorCanConfirmInternal(schemaObj, draftEdits, uiState);
// };

const getSchemaNameValidatorInternal = (existingNames: Set<string> | null) =>
  z
    .string()
    .trim()
    .min(1, 'Name cannot be empty')
    .max(
      SCHEMA_NAME_MAX_LENGTH,
      `Schema name cannot exceed ${SCHEMA_NAME_MAX_LENGTH} characters.`,
    )
    .superRefine((value, ctx) => {
      if (existingNames?.has(value.toLowerCase())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Schema with name ${value} is already defined`,
          fatal: true,
        });
        return z.NEVER;
      }
    });

export const useSchemaNameValidator = (currentName: string | undefined) => {
  const { permittedSchemas } = useDataModelerPermittedSchemas();
  const schemaStates = useAppSelector(
    (state) => state.dataModeler.schemaStates,
  );
  const existingRenamed = useMemo(() => {
    const renamedSchemas: { [k: string]: string | undefined } = {};
    Object.keys(schemaStates).forEach((schemaKey) => {
      const updatedName = schemaStates[schemaKey]?.draftEdits?.updatedName;
      if (updatedName) {
        renamedSchemas[schemaKey] = updatedName;
      }
    });
    return renamedSchemas;
  }, [schemaStates]);

  return useMemo(() => {
    const currentNamesSet = permittedSchemas.map(
      (schema) => existingRenamed[schema.id] ?? schema.name.toLowerCase(),
    );
    const permittedSchemasSet = new Set(
      currentNamesSet.filter((name) => name !== currentName?.toLowerCase()),
    );
    const schemaNameValidator = z.object({
      value: getSchemaNameValidatorInternal(permittedSchemasSet),
    });
    const knownSchemaKeys = permittedSchemas.map((schema) => schema.id);
    return {
      schemaNameValidator,
      knownSchemaKeys,
    };
  }, [permittedSchemas, currentName, existingRenamed]);
};

const getSubjectNameValidatorInternal = (existingNames: Set<string> | null) =>
  z
    .string()
    .trim()
    .min(1, 'Name cannot be empty')
    .max(
      SCHEMA_NAME_MAX_LENGTH,
      `Subject name cannot exceed ${SCHEMA_NAME_MAX_LENGTH} characters.`,
    )
    .superRefine((value, ctx) => {
      if (existingNames?.has(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Subject with name ${value} is already defined`,
          fatal: true,
        });
        return z.NEVER;
      }
    });

export const useSubjectNameValidatorFunc = () => {
  const draftEdits = useAppSelector(dataModelerSelectCurrentDraftEdits);
  const schemaObj = useAppSelector(dataModelerSelectCurrentSchemaObj);
  const allSubjects = useMemo(
    () => [
      ...(schemaObj?.subjects.map((subj) => {
        return {
          subjectKey: subj.subjectKey,
          subjectName: subj.name,
        };
      }) ?? []),
      ...(draftEdits?.addedSubjects.map((subj) => {
        return {
          subjectKey: subj.subjectKey,
          subjectName: subj.input.subjectName,
        };
      }) ?? []),
    ],
    [draftEdits?.addedSubjects, schemaObj?.subjects],
  );
  return useMemo(() => {
    return (currentSubjectKey?: string) => {
      const filteredSubjectNames = new Set(
        allSubjects
          .filter((subj) => subj.subjectKey !== currentSubjectKey)
          .map(({ subjectName }) => subjectName),
      );
      return z.object({
        value: getSubjectNameValidatorInternal(filteredSubjectNames),
      });
    };
  }, [allSubjects]);
};

export const getFunnelPartitionNameValidator = (
  existingNames: string[] | null,
) =>
  z
    .string()
    .trim()
    .min(1, 'Name cannot be empty')
    .max(
      SCHEMA_NAME_MAX_LENGTH,
      `Partition name cannot exceed ${SCHEMA_NAME_MAX_LENGTH} characters.`,
    )
    .superRefine((value, ctx) => {
      if (existingNames?.includes(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Partition with name ${value} is already defined`,
          fatal: true,
        });
        return z.NEVER;
      }
    });

export const rawSqlValidator = z.string().superRefine((value, ctx) => {
  const errors = validateRawSql(value);
  if (errors.length > 0) {
    const err = errors[0];
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      // XXX our internal start position is 0-based, covert to 1-based for human display.
      message: `Error at position ${err.start + 1} (${err.token}): ${err.message}`,
      fatal: true,
    });
    return z.NEVER;
  }
});

export const validationErrorToString = <T>(error: ZodError<T>): string => {
  return error.errors.map((e) => e.message).join(', ');
};
